import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  sendPasswordResetEmail,
  sendEmailVerification,
  verifyPasswordResetCode as fbVerifyPasswordResetCode,
  confirmPasswordReset as fbConfirmPasswordReset,
  ActionCodeSettings,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  ConfirmationResult,
  signOut,
  updateProfile,
  User,
} from 'firebase/auth';
import { auth } from './config';

const googleProvider = new GoogleAuthProvider();

export const registerWithEmail = async (
  email: string,
  password: string,
  displayName: string,
): Promise<User> => {
  const credential = await createUserWithEmailAndPassword(auth, email, password);
  await updateProfile(credential.user, { displayName });
  await sendEmailVerification(credential.user);
  return credential.user;
};

export const loginWithEmail = async (email: string, password: string): Promise<User> => {
  const credential = await signInWithEmailAndPassword(auth, email, password);
  return credential.user;
};

export const loginWithGoogle = async (): Promise<User> => {
  const credential = await signInWithPopup(auth, googleProvider);
  return credential.user;
};

export const sendPasswordReset = async (email: string): Promise<void> => {
  const actionCodeSettings: ActionCodeSettings = {
    url: `${window.location.origin}/reset-password`,
    handleCodeInApp: true,
  };
  try {
    await sendPasswordResetEmail(auth, email, actionCodeSettings);
  } catch {
    // Fallback if domain is not whitelisted in actionCodeSettings
    await sendPasswordResetEmail(auth, email);
  }
};

export const verifyResetCode = async (code: string): Promise<string> => {
  return await fbVerifyPasswordResetCode(auth, code);
};

export const resetPasswordWithCode = async (code: string, newPassword: string): Promise<void> => {
  await fbConfirmPasswordReset(auth, code, newPassword);
};

export const sendVerificationEmail = async (user?: User | null): Promise<void> => {
  const currentUser = user || auth.currentUser;
  if (!currentUser) throw new Error('No authenticated user found');
  await sendEmailVerification(currentUser);
};

export const setupRecaptcha = (containerId: string): RecaptchaVerifier => {
  if (typeof window !== 'undefined' && (window as any).recaptchaVerifier) {
    try {
      (window as any).recaptchaVerifier.clear();
    } catch {}
  }
  const appVerifier = new RecaptchaVerifier(auth, containerId, {
    size: 'invisible',
    callback: () => {
      // reCAPTCHA solved
    },
  });
  if (typeof window !== 'undefined') {
    (window as any).recaptchaVerifier = appVerifier;
  }
  return appVerifier;
};

export const sendPhoneOtp = async (
  phoneNumber: string,
  appVerifier: RecaptchaVerifier,
): Promise<ConfirmationResult> => {
  return await signInWithPhoneNumber(auth, phoneNumber, appVerifier);
};

export const logout = async (): Promise<void> => {
  await signOut(auth);
};

export const getCurrentUser = (): User | null => auth.currentUser;
